/**
 * Application chrome: the rail, the header, the content frame.
 *
 * finixt-ui's AppLayout owns the geometry and publishes the rail's collapse
 * state; the rail, the header, and the drawer are this app's, drawn as
 * finixt-ui draws them. Collapse persists across reloads. At or below
 * finixt-ui's medium breakpoint the rail is glyphs only whatever the stored
 * state, since the package's own breakpoints file narrows the rail there;
 * below its compact breakpoint the rail leaves the layout and opens as a
 * drawer from the header.
 */
import { listAgents } from '@optum/api-client/endpoints/agents';
import { listOrgCollections } from '@optum/api-client/endpoints/collections';
import { listGroups } from '@optum/api-client/endpoints/groups';
import { listOrgSecrets } from '@optum/api-client/endpoints/organization-secrets';
import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';
import { listRegistrations } from '@optum/api-client/endpoints/registrations';
import { getAdminOrganization } from '@optum/api-client/endpoints/organizations';
import { listTasks } from '@optum/api-client/endpoints/tasks';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext.js';
import { qk } from '../../lib/query-keys.js';
import { useCurrentOrgId } from '../../lib/use-current-org.js';
import { AppLayout, NavSection, usePersistedState } from '../../ui/finixt.js';
import { MobileDrawer } from '../../ui/MobileDrawer.js';
import { NavLink, type NavBadge } from '../../ui/NavLink.js';
import { Rail } from '../../ui/Rail.js';
import { useMediaQuery } from '../../ui/useMediaQuery.js';
import { AccountMenu } from './AccountMenu.js';
import { useBreadcrumbTailValue } from './BreadcrumbTailContext.js';
import { CommandPalette } from './CommandPalette.js';
import { Header } from './Header.js';
import { orgNav, PLATFORM_NAV, resolveCrumbs, type NavGroup } from './nav-config.js';
import {
  FORM_PARAM,
  parseFormParam,
  parseRecordParam,
  RECORD_PARAM,
  RecordStackContext,
  type RecordRef,
} from './record.js';
import { RecordPanel } from './RecordPanel.js';
import { TenantSwitcher } from './TenantSwitcher.js';

const RAIL_KEY = 'optum.admin.rail';
const PANEL_KEY = 'optum.admin.panel';
const MEDIUM = '(max-width: 900px)';
const COMPACT = '(max-width: 640px)';

/** What a rail row shows beside its label: a size, or a state that needs doing. */
interface NavMark {
  count?: number | undefined;
  badges?: readonly NavBadge[] | undefined;
}

type NavMarks = Record<string, NavMark | undefined>;

/**
 * Marks on the tenant nav. Sizes, not states: a count only earns a tone when
 * it means something needs doing, as on the platform nav. Cheap list calls the
 * pages themselves already make, so this shares their cache.
 */
function useOrgNavMarks(orgId: string | null): NavMarks {
  const enabled = Boolean(orgId);
  const base = `/orgs/${orgId}`;
  const agents = useQuery({
    queryKey: qk.agents(orgId ?? ''),
    queryFn: () => listAgents({ organization_id: orgId ?? '' }),
    enabled,
    staleTime: 60_000,
  });
  const collections = useQuery({
    queryKey: qk.collections(orgId ?? ''),
    queryFn: () => listOrgCollections(orgId ?? ''),
    enabled,
    staleTime: 60_000,
  });
  const groups = useQuery({
    queryKey: qk.groups(orgId ?? ''),
    queryFn: () => listGroups(orgId ?? ''),
    enabled,
    staleTime: 60_000,
  });
  const secrets = useQuery({
    queryKey: qk.orgSecrets(orgId ?? ''),
    queryFn: () => listOrgSecrets(orgId ?? ''),
    enabled,
    staleTime: 60_000,
  });
  if (!orgId) return {};
  return {
    [`${base}/agents`]: { count: agents.data?.length ?? 0 },
    [`${base}/collections`]: { count: collections.data?.nodes.length ?? 0 },
    [`${base}/groups`]: { count: groups.data?.length ?? 0 },
    [`${base}/secrets`]: { count: (secrets.data ?? []).filter((x) => x.is_set).length },
  };
}

/**
 * Marks on the platform nav: what is waiting on a decision, amber, and what
 * is currently broken, red. The two carry different urgency and one neutral
 * figure would flatten them together.
 */
function usePlatformNavMarks(): NavMarks {
  const registrations = useQuery({
    queryKey: qk.registrations(),
    queryFn: listRegistrations,
    staleTime: 60_000,
  });
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
    staleTime: 60_000,
  });
  const failed = useQuery({
    queryKey: qk.tasks({ status: ['failed'] }),
    queryFn: () => listTasks({ status: ['failed'] }),
    staleTime: 60_000,
  });
  // Both halves of the review queue wait on the same decision, so they share
  // one amber count. The board is where both counts are read; Tasks is where
  // the red one is worked, and it carries no badge of its own.
  const pending =
    (registrations.data ?? []).filter((r) => r.status === 'pending').length +
    (partners.data ?? []).filter((p) => p.status === 'pending').length;
  const broken = (failed.data ?? []).length;
  const badges: NavBadge[] = [
    ...(pending > 0 ? [{ value: pending, tone: 'warn' as const }] : []),
    ...(broken > 0 ? [{ value: broken, tone: 'danger' as const }] : []),
  ];
  return {
    '/overview': badges.length > 0 ? { badges } : undefined,
  };
}

/**
 * Whether a row owns the highlight. A longer entry that also matches wins, so
 * `/plugins` stays quiet on `/plugins/capability/contextual-qa` when a more
 * specific row exists.
 */
function isActive(pathname: string, to: string, all: readonly string[]): boolean {
  if (pathname === to) return true;
  if (!pathname.startsWith(`${to}/`)) return false;
  return !all.some(
    (other) => other !== to && other.length > to.length && pathname.startsWith(other),
  );
}

/** The rail rows for one workspace, as a flat list so the rail can clone onto each. */
function navRows(groups: readonly NavGroup[], pathname: string, marks: NavMarks): ReactNode[] {
  const all = groups.flatMap((g) => g.items.map((i) => i.to));
  return groups.flatMap((group, index) => [
    group.label ? <NavSection key={`section-${index}`} label={group.label} /> : null,
    ...group.items.map((item) => {
      const mark = marks[item.to];
      const count = mark?.count;
      return (
        <NavLink
          key={item.to}
          to={item.to}
          icon={item.icon}
          label={item.label}
          active={isActive(pathname, item.to, all)}
          badges={mark?.badges}
          count={count !== undefined && count > 0 ? count : undefined}
        />
      );
    }),
  ]);
}

export function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const orgId = useCurrentOrgId();
  const { user, isPlatformAdmin } = useAuth();
  const tail = useBreadcrumbTailValue();

  // Fetched here rather than taken from the breadcrumb tail: the tail carries
  // the entity name on detail pages, so a nested route would otherwise show
  // the agent's name where the tenant's belongs.
  const org = useQuery({
    queryKey: qk.org(orgId ?? ''),
    queryFn: () => getAdminOrganization(orgId ?? ''),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
  });
  const orgName = org.data?.name;
  const platformMarks = usePlatformNavMarks();
  const orgMarks = useOrgNavMarks(orgId);
  const marks = orgId ? orgMarks : platformMarks;
  const groups = orgId ? orgNav(orgId) : PLATFORM_NAV;
  const crumbs = resolveCrumbs(pathname, orgName);
  // A detail page's entity name extends the trail rather than replacing it.
  const trail = tail ? [...crumbs, { label: tail }] : crumbs;

  const [stored, setStored] = usePersistedState(RAIL_KEY, false);
  const medium = useMediaQuery(MEDIUM);
  const compact = useMediaQuery(COMPACT);
  const collapsed = !compact && (medium || stored);
  const toggle = useCallback(() => setStored((c) => !c), [setStored]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);

  // The record panel. The URL names the open record; the stack beneath it is
  // here, and both go when the route changes: a record belongs to the page
  // that opened it.
  const [params, setParams] = useSearchParams();
  const record = parseRecordParam(params.get(RECORD_PARAM));
  const form = parseFormParam(params.get(FORM_PARAM));
  const [below, setBelow] = useState<readonly RecordRef[]>([]);
  const [panelWidth, setPanelWidth] = usePersistedState(PANEL_KEY, 352);
  const stack = useMemo(() => ({ below, setBelow }), [below]);
  const closeRecord = useCallback(() => {
    setBelow([]);
    const next = new URLSearchParams(params);
    next.delete(RECORD_PARAM);
    next.delete(FORM_PARAM);
    setParams(next, { replace: true });
  }, [params, setParams]);
  useEffect(() => {
    setBelow([]);
  }, [pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'b' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggle();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  const rail = (
    <Rail
      onSearch={openPalette}
      header={
        <>
          {orgId ? <NavLink to="/overview" icon="arrow_back" label="Platform" /> : null}
          <TenantSwitcher orgId={orgId ?? undefined} orgName={orgName} />
        </>
      }
      footer={<AccountMenu />}
    >
      {navRows(groups, pathname, marks)}
    </Rail>
  );

  return (
    <RecordStackContext.Provider value={stack}>
    <AppLayout
      rail={compact ? undefined : rail}
      railCollapsed={collapsed}
      onRailToggle={toggle}
      panel={<RecordPanel />}
      panelOpen={record !== null || form !== null}
      onPanelToggle={closeRecord}
      panelStripTone="neutral"
      panelStripIcon="info"
      panelResizable
      panelWidth={panelWidth}
      onPanelWidthChange={setPanelWidth}
      header={
        <Header
          trail={trail}
          toggle={compact || medium ? undefined : { collapsed, onToggle: toggle }}
          onOpenDrawer={compact ? () => setDrawerOpen(true) : undefined}
          role={user ? (isPlatformAdmin ? 'admin' : 'viewer') : undefined}
        />
      }
    >
      <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex' }}>{children}</div>
      {compact ? (
        <MobileDrawer open={drawerOpen} onOpenChange={setDrawerOpen} label="Navigation">
          {rail}
        </MobileDrawer>
      ) : null}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </AppLayout>
    </RecordStackContext.Provider>
  );
}
